import React from "react";
import { Box, Flex, HStack, Icon, Input, Text } from "@chakra-ui/react";
import { PiWarningDuotone } from "react-icons/pi";
import { drgba, EASE } from "../../../components/glassUi";

// ── Błąd pola (spójny alert zamiast gołego Text variant="error") ──
export const FieldError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <HStack align="flex-start" spacing="8px" mt="6px" bg={drgba("#dc2626", 0.08)} border={`1px solid ${drgba("#dc2626", 0.22)}`} borderRadius="12px" p="9px 12px">
    <Icon as={PiWarningDuotone} color="#dc2626" boxSize="16px" mt="1px" flexShrink={0} />
    <Text fontSize="13px" color="#b91c1c" fontWeight="600" lineHeight="1.45">
      {children}
    </Text>
  </HStack>
);

// ── Kolorowe pole liczbowe z ikoną/labelem/suffixem ──
export const NumberInputChip: React.FC<{
  value: number | string | undefined;
  onChange: (v: string) => void;
  color?: string;
  icon?: React.ElementType;
  label?: string;
  suffix?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  w?: string;
}> = ({ value, onChange, color = "#430098", icon, label, suffix, placeholder, min, max, w = "120px" }) => (
  <HStack spacing="10px" align="center">
    {icon && (
      <Flex w="38px" h="38px" borderRadius="12px" bg={drgba(color, 0.12)} align="center" justify="center" flexShrink={0}>
        <Icon as={icon} boxSize="18px" color={color} />
      </Flex>
    )}
    <Input
      type="number"
      min={min}
      max={max}
      w={w}
      h="46px"
      placeholder={placeholder}
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      bg={drgba(color, 0.06)}
      border="1.5px solid"
      borderColor={drgba(color, 0.24)}
      borderRadius="12px"
      fontWeight="800"
      color="#241536"
      transition={`all ${EASE}`}
      _hover={{ borderColor: drgba(color, 0.45) }}
      _focusVisible={{ borderColor: color, boxShadow: `0 0 0 3px ${drgba(color, 0.18)}` }}
    />
    {(label || suffix) && (
      <Text color="#5a5468" fontWeight="700" fontSize="14px">
        {suffix || label}
      </Text>
    )}
  </HStack>
);

// ── Pusty stan kroku (glass + duotone chip + CTA jako children) ──
export const StepEmptyState: React.FC<{ icon: React.ElementType; title: string; desc?: string; color?: string; children?: React.ReactNode }> = ({ icon, title, desc, color = "#430098", children }) => (
  <Flex direction="column" align="center" justify="center" py="42px" px="20px" borderRadius="16px" border={`1.5px dashed ${drgba(color, 0.28)}`} bg={drgba(color, 0.03)} textAlign="center">
    <Flex w="56px" h="56px" borderRadius="16px" bg={drgba(color, 0.12)} align="center" justify="center" mb="13px">
      <Icon as={icon} boxSize="28px" color={color} />
    </Flex>
    <Text color="#241536" fontWeight="900" fontSize="16px" mb="3px" letterSpacing="-0.01em">{title}</Text>
    {desc && <Text color="#6b6480" fontSize="13.5px" maxW="360px" mb={children ? "16px" : 0}>{desc}</Text>}
    {children}
  </Flex>
);

const pad = (n: number) => String(n).padStart(2, "0");
const fmtPreview = (v: string): string => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
  } catch {
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
};

// ── Pole datetime-local z ikoną, semantycznym kolorem i podglądem pl-PL ──
export const DateInputWithIcon: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  min?: string;
  max?: string;
  color?: string;
  icon: React.ElementType;
  invalid?: boolean;
}> = ({ value, onChange, onBlur, min, max, color = "#430098", icon, invalid }) => {
  const preview = fmtPreview(value);
  const bc = invalid ? "#e51f44" : drgba(color, 0.24);
  return (
    <Box>
      <Flex
        align="center"
        gap="10px"
        bg="rgba(255,255,255,0.72)"
        border="1.5px solid"
        borderColor={bc}
        borderRadius="12px"
        px="12px"
        h="48px"
        transition={`all ${EASE}`}
        _focusWithin={{ borderColor: invalid ? "#e51f44" : color, boxShadow: `0 0 0 3px ${drgba(invalid ? "#e51f44" : color, 0.16)}` }}
      >
        <Icon as={icon} boxSize="18px" color={invalid ? "#e51f44" : color} flexShrink={0} />
        <Input
          type="datetime-local"
          value={value}
          min={min}
          max={max}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          variant="unstyled"
          fontSize="15px"
          fontWeight="700"
          color="#241536"
          sx={{ "&::-webkit-calendar-picker-indicator": { cursor: "pointer", opacity: 0.55 } }}
        />
      </Flex>
      {preview && (
        <Text fontSize="12px" color={drgba(color, 0.85)} fontWeight="700" mt="6px" pl="4px" textTransform="capitalize">
          {preview}
        </Text>
      )}
    </Box>
  );
};
